import React from 'react';
import './ErrorBoundary.css';

/**
 * ErrorBoundary — reusable React error boundary.
 *
 * React error boundaries only catch errors thrown while RENDERING the
 * component tree below them (render(), lifecycle methods, and constructors
 * of children). They do NOT catch:
 *   - errors in event handlers (onClick, onSubmit, ...) — React invokes
 *     those outside the boundary, so a throwing handler is not routed here.
 *     Wrap handler bodies in try/catch when they can throw.
 *   - errors in asynchronous code (setTimeout, setInterval, Promise
 *     callbacks / async fetch handlers, requestAnimationFrame, ...).
 *   - errors thrown in the boundary itself.
 * So this is a safety net for render-time crashes (e.g. the #65
 * `platform()` ReferenceError that white-screened every tab for every
 * logged-in user), NOT a general error trap for handlers or async code.
 *
 * Variants:
 *   - default (app root, in index.js): full-screen fallback — "Something
 *     went wrong", a Reload button (full page reload), and a copy-error
 *     button.
 *   - inline (per screen/tab and per Settings tab): a compact
 *     "This section failed to load" card with "Try again" (resets the local
 *     error state, which remounts the section's subtree) and a copy-error
 *     button, so the nav bar and sibling tabs keep working.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const label = this.props.label || 'app';
    // Persist the component stack so the copy-error text and any future
    // report include it (setState here is legal in componentDidCatch).
    if (info && info.componentStack) {
      this.setState({ info });
    }
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary] "${label}" failed to render: ${error && error.message ? error.message : String(error)}`,
      '\nComponent stack:\n' + (info && info.componentStack ? info.componentStack : '(no component stack)'),
    );

    // Best-effort client error report. As of this PR there is NO existing
    // client-error / logging endpoint in the backend (audited the /api/*
    // route table), so the console.error above IS the report — do not invent
    // a backend endpoint here. If a client-error endpoint is added later,
    // fire-and-forget {label, message, componentStack} to it from
    // _reportError, wrapped in try/catch.
    this._reportError(error, info);
  }

  _reportError(error, info) {
    // No existing client-error endpoint — intentionally a no-op so wiring
    // one in later is a one-line change. See comment in componentDidCatch.
  }

  _handleRetry = () => {
    // Reset the local error state so React re-renders (effectively remounts)
    // the children subtree — the section reloads itself in place without a
    // full page navigation. Children start back from fresh state.
    this.setState({ error: null, info: null, copied: false });
  };

  _handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  _handleCopy = async () => {
    const text = this._errorText();
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (_) {
      /* fall through to the legacy textarea path below */
    }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) {
        ok = false;
      }
    }
    this.setState({ copied: ok });
  };

  _errorText() {
    const e = this.state.error;
    const label = this.props.label || 'app';
    const stack = this.state.info && this.state.info.componentStack ? this.state.info.componentStack : '';
    return `[Truva ${label} error]\n${e && e.message ? e.message : String(e)}${stack ? '\n\n' + stack : ''}`;
  }

  _renderFull() {
    return (
      <div className="truva-error-boundary truva-error-boundary--app" role="alert">
        <div className="truva-error-card">
          <div className="truva-error-card-icon" aria-hidden="true">⚠️</div>
          <h1 className="truva-error-title">Something went wrong</h1>
          <p className="truva-error-subtitle">
            An unexpected error stopped Truva from loading. Your sign-in,
            contacts and settings are safe.
          </p>
          <pre className="truva-error-detail" data-testid="truva-error-detail">
            {this.state.error && this.state.error.message ? this.state.error.message : String(this.state.error)}
          </pre>
          <div className="truva-error-actions">
            <button type="button" className="truva-error-btn truva-error-btn--primary" onClick={this._handleReload}>
              Reload
            </button>
            <button type="button" className="truva-error-btn" onClick={this._handleCopy}>
              {this.state.copied ? 'Copied' : 'Copy error'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  _renderInline() {
    const label = this.props.label || 'this section';
    return (
      <div className="truva-error-inline" role="alert" data-testid="truva-error-inline">
        <div className="truva-error-inline-head">
          <strong>This section failed to load</strong>
          <span className="truva-error-inline-label">{label}</span>
        </div>
        <pre className="truva-error-detail" data-testid="truva-error-detail">
          {this.state.error && this.state.error.message ? this.state.error.message : String(this.state.error)}
        </pre>
        <div className="truva-error-actions">
          <button type="button" className="truva-error-btn truva-error-btn--primary" onClick={this._handleRetry}>
            Try again
          </button>
          <button type="button" className="truva-error-btn" onClick={this._handleCopy}>
            {this.state.copied ? 'Copied' : 'Copy error'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.error) {
      return this.props.inline ? this._renderInline() : this._renderFull();
    }
    // No wrapper element on the happy path — children render exactly as they
    // would without the boundary, so this adds zero layout/DOM impact.
    return this.props.children;
  }
}
